import { Box, Button, ButtonGroup, Checkbox, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CardBgcolor, ListItemBackground, MainBorderRadius } from '../../app/AppStyles';
import { DisplayName } from '../../app/AppUtils';
import ContentLoader from '../subcomponents/ContentLoader';
import ContentSelector from '../subcomponents/ContentSelector';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../subcomponents/alertSlice';
import MessagesClasses from './MessagesClasses';
import MessagesStudents from './MessagesStudents';
import { fetchMessageActivities, fetchMessageClasses, fetchMessagePerformances, fetchMessageStudents, fetchMessageStudentsPerformance, postMessage, resetMessageStatus, resetPerformances, setMsgReceivers, setMsgStudentFilter } from './messageSlice';
import RichTextEditor from '../subcomponents/RichTextEditor';

export default function MessageSendTab() {
    const dispatch = useDispatch();
    const radioRef = useRef();
    const quillRef = useRef();

    const [messageSubject, setMessageSubject] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [messageMode, setMessageMode] = useState('Class');
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [selectedPerformances, setSelectedPerformances] = useState([]);
    const [selectedActivity, setSelectedActivity] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [autoAddStudents, setAutoAddStudents] = useState(true);

    const { activeDepartment } = useSelector((state) => state.settings.theme);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { msgClassesStatus, msgActivities, msgActivitiesStatus, msgStudents, msgStudentsStatus, msgReceivers, postMessageStatus, msgPerformances, msgPerformancesStatus } = useSelector((state) => state.messages);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { user } = useSelector((state) => state.user);
    const { appSettings } = useSelector((state) => state.settings.app);

    useEffect(() => {
        dispatch(fetchMessageClasses());
    }, [dispatch]);

    useEffect(() => {
        if (selectedActivity.length > 0) {
            dispatch(fetchMessagePerformances(selectedActivity[0].activityId));
        } else if (selectedActivity.length === 0) {
            dispatch(resetPerformances())
        }
    }, [selectedActivity]);

    const resetClick = () => {
        dispatch(resetMessageStatus());
        setSelectedClasses([]);
        setSelectedStudents([]);
        setSelectedActivity([]);
        setSelectedPerformances([]);
        setMessageSubject("");
        setMessageBody("");
        dispatch(setMsgStudentFilter(""));

        if (messageMode === "Student")
            dispatch(fetchMessageStudents());
    };

    const handleRadioClick = (value) => {
        const departmentId = siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null;

        dispatch(resetMessageStatus());

        if (value === "Class")
            dispatch(fetchMessageClasses());

        if (value === "Performance")
            dispatch(fetchMessageActivities(departmentId));

        if (value === "Student")
            dispatch(fetchMessageStudents());

        dispatch(setMsgStudentFilter(""));
        setSelectedClasses([]);
        setSelectedPerformances([]);
        setSelectedStudents([]);
        setSelectedActivity([]);
        setMessageMode(value);
    };

    const handleSendClick = () => {
        if (!msgReceivers) return
        if (!messageSubject) return;
        if (!messageBody) return;

        var studentNumbers = msgReceivers.map(function (item) {
            return item['studentNumber'];
        });

        var studentIds = msgReceivers.map(function (item) {
            return item['studentId'];
        });

        var messageObject = {
            title: messageSubject,
            body: messageBody,
            subject: messageSubject,
            sentFrom: `${user.number}`,
            displayName: `${DisplayName(user.firstName, user.middleName, user.lastName)}`,
            direction: 'Sent',
            type: 'App',
            studentNummer: studentNumbers,
            studentIds: studentIds,
            notificationMessage: `${resourceFinal[appLanguage].messages.NOTIFICATION_NEW_MESSAGE}`,
        };

        dispatch(postMessage(messageObject));
    };

    // post & put status callback
    useEffect(() => {
        if (postMessageStatus === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SENT_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetMessageStatus());

            setSelectedClasses([]);
            setSelectedStudents([]);
            setMessageSubject("");
            setMessageBody("");
            dispatch(setMsgStudentFilter(""));

            if (messageMode === "Student")
                dispatch(fetchMessageStudents());
        } else if (postMessageStatus === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SENT_ERROR}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetMessageStatus());
        };
    }, [postMessageStatus]);

    useEffect(() => {
        if (msgStudentsStatus === "success" && autoAddStudents && messageMode !== "Student") {
            setSelectedStudents(msgStudents);
            dispatch(setMsgReceivers(msgStudents));
        }
    }, [msgStudentsStatus, autoAddStudents, messageMode]);

    const handleFillClick = () => {
        dispatch(fetchMessageStudentsPerformance(selectedPerformances.map(p => p.performanceId).join(',')));
    };

    return (
        <Stack spacing={1} sx={{ width: 1 }}>
            <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                <FormControl onClick={(e) => handleRadioClick(e.target.value)} ref={radioRef}>
                    <FormLabel>{resourceFinal[appLanguage].words.SELECT} {resourceFinal[appLanguage].words.MODE.toLowerCase()}</FormLabel>
                    <RadioGroup defaultValue="Class">
                        <FormControlLabel value="Class" control={<Radio />} label={resourceFinal[appLanguage].words.CLASSES} />
                        <FormControlLabel value="Performance" control={<Radio />} label={resourceFinal[appLanguage].words.PERFORMANCES} />
                        <FormControlLabel value="Student" control={<Radio />} label={resourceFinal[appLanguage].words.STUDENTS} />
                    </RadioGroup>
                </FormControl>
            </Box>
            {messageMode === "Class" && (
                <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                    <ContentLoader
                        successCondition={msgClassesStatus === "success"}
                        errorCondition={msgClassesStatus === "failed"}
                        content={
                            <MessagesClasses selectedClasses={selectedClasses} setSelectedClasses={setSelectedClasses} />
                        }
                    />
                </Box>
            )}
            {messageMode === "Performance" && (
                <>
                    <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                        <ContentLoader
                            successCondition={msgActivitiesStatus === "success"}
                            errorCondition={msgActivitiesStatus === "failed"}
                            content={
                                <ContentSelector
                                    content={msgActivities}
                                    contentId="activityId"
                                    contentTitle={resourceFinal[appLanguage].words.ACTIVITIES}
                                    contentHeaders={[resourceFinal[appLanguage].words.NAME]}
                                    contentRows={["activityName"]}
                                    selectedContent={selectedActivity}
                                    setSelectedContent={setSelectedActivity}
                                />
                            }
                        />
                    </Box>
                    <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                        {selectedActivity.length > 0 &&
                            <ContentLoader
                                successCondition={msgPerformancesStatus === "success"}
                                errorCondition={msgPerformancesStatus === "failed"}
                                content={
                                    <ContentSelector
                                        content={msgPerformances}
                                        contentId="performanceId"
                                        contentTitle={resourceFinal[appLanguage].words.PERFORMANCES}
                                        contentHeaders={[
                                            resourceFinal[appLanguage].words.NUMBER,
                                            (appSettings?.appVersion === 2 || siomSettings?.enablePerformanceName) ? resourceFinal[appLanguage].words.NAME : "",
                                            resourceFinal[appLanguage].words.LOCATION,
                                            `${resourceFinal[appLanguage].words.START}${resourceFinal[appLanguage].words.DATE.toLowerCase()}`,
                                            `${resourceFinal[appLanguage].words.END}${resourceFinal[appLanguage].words.DATE.toLowerCase()}`,
                                            resourceFinal[appLanguage].words.SUBSCRIPTIONS
                                        ]}
                                        contentRows={[
                                            "performanceNumber",
                                            (appSettings?.appVersion === 2 || siomSettings?.enablePerformanceName) ? "performanceName" : "",
                                            "locationName",
                                            "startDate",
                                            "endDate",
                                            "subscriptionsCount"
                                        ]}
                                        selectedContent={selectedPerformances}
                                        setSelectedContent={setSelectedPerformances}
                                        multiple
                                        defaultRowsPerPage={50}
                                    />
                                }
                            />
                        }
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                        <Button
                            disabled={selectedPerformances.length <= 0}
                            sx={{ width: 180, mr: 1 }}
                            variant="outlined"
                            color="secondary"
                            onClick={handleFillClick}>
                            {resourceFinal[appLanguage].words.FILL} {resourceFinal[appLanguage].words.STUDENTS.toLowerCase()}
                        </Button>
                        <FormControlLabel control={
                            <Checkbox
                                defaultChecked={autoAddStudents}
                                disabled={selectedPerformances.length <= 0}
                                onChange={(e) => {
                                    setAutoAddStudents(e.target.checked ? true : false);
                                }} />
                        } label={resourceFinal[appLanguage].messages.MESSAGE_AUTO_FILL} />
                    </Box>
                </>
            )}
            <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                <MessagesStudents selectedStudents={selectedStudents} setSelectedStudents={setSelectedStudents} />
            </Box>
            <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                <Typography variant="body1">{msgReceivers?.length > 1 ? resourceFinal[appLanguage].words.RECEIVERS : resourceFinal[appLanguage].words.RECEIVER} ({msgReceivers ? msgReceivers?.length : 0}): {msgReceivers?.map(n => n.studentNumber).join(', ')}</Typography>
            </Box>
            <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                <TextField
                    sx={{ ...ListItemBackground }}
                    fullWidth
                    value={messageSubject}
                    label={resourceFinal[appLanguage].words.SUBJECT}
                    onChange={(event) => setMessageSubject(event.target.value)}
                    variant="outlined"
                />
            </Box>
            <Box sx={{ p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                <RichTextEditor
                    ref={quillRef}
                    defaultValue={messageBody || ''}
                    onTextChange={(html) => setMessageBody(html)}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderRadius: MainBorderRadius, bgcolor: CardBgcolor }}>
                <ButtonGroup variant="outlined">
                    <Button disabled={!msgReceivers && !messageSubject && !messageBody} color="error" onClick={resetClick}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                    <Button disabled={!msgReceivers || !messageSubject || !messageBody} color="success" onClick={handleSendClick}>{resourceFinal[appLanguage].words.SEND} {resourceFinal[appLanguage].words.MESSAGE}</Button>
                </ButtonGroup>
            </Box>
        </Stack>
    );
}
